import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ImpersonationService } from 'src/app/core/auth/impersonation.service';
import { AuthService } from 'src/app/core/auth/auth.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ImpersonationConfirmDialogComponent,
  ImpersonationDialogData,
} from '../impersonation-confirm-dialog/impersonation-confirm-dialog.component';

@Component({
  selector: 'app-impersonate-button',
  templateUrl: './impersonate-button.component.html',
  styleUrls: ['./impersonate-button.component.scss'],
})
export class ImpersonateButtonComponent implements OnInit, OnDestroy {
  private readonly impersonationService = inject(ImpersonationService);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);

  @Input() userId!: number;
  @Input() userName!: string;
  @Input() userEmail!: string;
  @Input() userRole?: string;
  @Input() disabled: boolean = false;
  @Input() iconOnly: boolean = false;

  @Output() impersonationStarted = new EventEmitter<void>();

  isLoading = false;
  isImpersonationActive = false;
  impersonatingUserName: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    this.impersonationService.impersonationState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.isImpersonationActive = state.isActive;
        this.impersonatingUserName = state.impersonatedUser?.name ?? null;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isDisabled(): boolean {
    return this.disabled || this.isLoading || this.isImpersonationActive;
  }

  get tooltipText(): string {
    if (this.isImpersonationActive) {
      const name = this.impersonatingUserName || 'a user';
      return `Impersonation already active (${name}). End current impersonation to start another.`;
    }
    return 'Impersonate user: ' + this.userName;
  }

  /**
   * Check if current user can impersonate
   */
  canImpersonate(): boolean {
    const currentUser = this.authService.user;
    if (!currentUser) return false;

    // Only SUPERADMIN can impersonate
    return currentUser.roles?.includes('SUPERADMIN') || false;
  }

  /**
   * Open confirmation dialog and start impersonation
   */
  openImpersonationDialog(): void {
    if (!this.canImpersonate() || this.isDisabled) {
      return;
    }

    const dialogData: ImpersonationDialogData = {
      userId: this.userId,
      userName: this.userName,
      userEmail: this.userEmail,
      userRole: this.userRole,
      ttlMinutes: 60,
    };

    const dialogRef = this.dialog.open(ImpersonationConfirmDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: dialogData,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.startImpersonation(result.ttlMinutes, result.reason);
      }
    });
  }

  /**
   * Start impersonation
   */
  private startImpersonation(ttlMinutes: number, reason: string, location: number | null = null): void {
    this.isLoading = true;

    this.impersonationService
      .startImpersonation(this.userId, ttlMinutes,location, reason)
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.snackBar.open(
            `Impersonation started: ${this.userName}`,
            'Close',
            {
              duration: 5000,
              panelClass: ['success-snackbar'],
            }
          );

          this.impersonationStarted.emit();

          // Optionally reload the page to refresh context
          setTimeout(() => {
            window.location.reload();
          }, 500);
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error starting impersonation:', error);

          this.snackBar.open(
            error.message || 'Error starting impersonation',
            'Close',
            {
              duration: 7000,
              panelClass: ['error-snackbar'],
            }
          );
        },
      });
  }
}
